<script setup lang="ts">
import ArrowUpSvg from '@/assets/sprite/arrowUp.svg';
import ArrowDownSvg from '@/assets/sprite/arrowDown.svg';

const emit = defineEmits<{
    close: [];
}>();

const { $lrud } = useNuxtApp();
const emitterLrud = $lrud.emitter;

const onClose = () => emit('close');

const onRegister = async (event: any) => {
    await nextTick();
    $lrud.assignFocus(event);
};

/** Документ длинный, прокручиваем его постранично */
const SCROLL_RATIO = 0.6;

const doc = ref<HTMLElement>();
const isScrollableUp = ref(false);
const isScrollableDown = ref(false);

const syncEdges = () => {
    const el = doc.value;
    if (!el) return;

    isScrollableUp.value = el.scrollTop > 1;
    isScrollableDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
};

const scrollDoc = (sign: number) => {
    const el = doc.value;
    if (!el) return;

    // Без smooth: плавная прокрутка дёргается на телевизорах
    el.scrollTop += sign * Math.round(el.clientHeight * SCROLL_RATIO);
    syncEdges();
};

/** Где сейчас фокус: на документе или на кнопке «Закрыть» */
const focusTarget = ref<'doc' | 'button'>('doc');
const isDocFocused = computed(() => focusTarget.value === 'doc');

const onCallbackHandkeKey: Parameters<typeof emitterLrud.on>[0] = ({ direction }) => {
    if (direction === 'back') {
        onClose();
        emitterLrud.stop();
        return;
    }

    // Влево и вправо в попапе идти некуда, фокус наружу не выпускаем
    if (direction === 'left' || direction === 'right') {
        emitterLrud.stop();
        return;
    }

    /*
     * Пока документ можно прокручивать — крутим сами: глобальный обработчик lrud
     * делает preventDefault, нативной прокрутки нет. Когда прокрутка кончилась,
     * пропускаем событие дальше, и lrud переводит фокус на кнопку «Закрыть» (и обратно).
     */
    if (direction === 'down' && isDocFocused.value && isScrollableDown.value) {
        scrollDoc(1);
        emitterLrud.stop();
        return;
    }

    if (direction === 'up' && isDocFocused.value && isScrollableUp.value) {
        scrollDoc(-1);
        emitterLrud.stop();
    }
};

onBeforeMount(() => {
    emitterLrud.on(onCallbackHandkeKey);
});

onMounted(async () => {
    await nextTick();
    syncEdges();
});

onBeforeUnmount(() => {
    emitterLrud.off(onCallbackHandkeKey);
});
</script>

<template>
    <PopupBase>
        <LrudNode orientation="vertical" class="popup-terms" :parent="$lrud.base.rootNode.id">
            <LrudNode
                isFocusable
                defaultFocus
                class="popup-terms__scroll"
                @register="onRegister"
                @focused="focusTarget = 'doc'"
            >
                <div ref="doc" class="popup-terms__doc" @scroll="syncEdges">
                    <div class="terms" dir="rtl" lang="he">
                        <h1>תנאי שירות ומדיניות פרטיות לפלטפורמת הטלוויזיה <bdi>APPRU</bdi></h1>
                        <p class="terms__intro">השימוש בשירות מהווה הסכמה לתנאים המפורטים להלן:</p>
                        <section class="terms__sec">
                            <h2>כללי</h2>
                            <ol class="terms__clauses" role="list">
                                <li>תנאים אלו חלים על כל המשתמשים בפלטפורמה, בכל סוג מכשיר.</li>
                                <li>
                                    <bdi>APPRU</bdi> שומרת לעצמה את הזכות לעדכן את התנאים בכל עת. במקרה של שינוי, נפרסם
                                    הודעה מתאימה. המשך שימוש בשירות לאחר פרסום התנאים המעודכנים יהווה הסכמה מחודשת
                                    לתנאים אלו. אנו ממליצים לעיין בתנאים מעת לעת על מנת להתעדכן בשינויים שחלו.
                                </li>
                                <li>תחילת השימוש בשירות נחשבת ממועד הכניסה הראשונה למערכת.</li>
                            </ol>
                        </section>
                        <section class="terms__sec">
                            <h2>שימוש בשירות</h2>
                            <ol class="terms__clauses" role="list">
                                <li>
                                    השירות מיועד לשימוש אישי ופרטי בלבד. למען הסר ספק, השירות אינו מיועד לשימוש מסחרי.
                                </li>
                                <li>אין להעביר או לשדר את התכנים ללא אישור מראש.</li>
                                <li><bdi>APPRU</bdi> אינה אחראית לתקלות הקשורות באינטרנט או בציוד הקצה.</li>
                                <li>
                                    <bdi>APPRU</bdi> אינה מתחייבת לכך שהשירות יהיה זמין תמיד, ללא שגיאות או תקלות, או
                                    שהתכנים יעמדו בציפיות המשתמש.
                                </li>
                                <li>
                                    אסור בהחלט להשתמש <span class="terms__nb">ב-<bdi>VPN</bdi></span
                                    >, שרתי פרוקסי או אמצעים אחרים לעקוף הגבלות גיאוגרפיות.
                                </li>
                                <li>אסור לנסות לפרוץ, לעקוף אמצעי הגנה טכניים, לפרק או לבצע הנדסה לאחור של השירות.</li>
                            </ol>
                        </section>
                        <section class="terms__sec">
                            <h2>דרישות טכניות</h2>
                            <ol class="terms__clauses" role="list">
                                <li>
                                    <bdi>APPRU</bdi> אינה מתחייבת לאיכות וידאו מסוימת. איכות הוידאו מתאימה אוטומטית
                                    למהירות החיבור.
                                </li>
                            </ol>
                        </section>
                        <section class="terms__sec">
                            <h2>רישוי וזכויות קניין רוחני</h2>
                            <ol class="terms__clauses" role="list">
                                <li>
                                    כל החומרים בפלטפורמה מוגנים בזכויות יוצרים ושייכים
                                    <span class="terms__nb">ל-<bdi>APPRU</bdi></span> או למעניקי הרישיונות.
                                </li>
                                <li>המשתמש מקבל רישיון מוגבל ולא בלעדי לצפייה בתכנים אך ורק בשטח מדינת ישראל.</li>
                                <li>אסור להקליט, להוריד, להעתיק או לשחזר תכנים מחוץ לפלטפורמה.</li>
                            </ol>
                        </section>
                        <section class="terms__sec">
                            <h2>פרטיות ואבטחת מידע</h2>
                            <ol class="terms__clauses" role="list">
                                <li>
                                    הפלטפורמה עשויה לשמור נתונים טכניים על כניסות, כתובות <bdi>IP</bdi> ופעולות משתמש.
                                </li>
                                <li>הנתונים נשמרים במאגר מאובטח בהתאם לחוק הגנת הפרטיות.</li>
                            </ol>
                        </section>
                        <section class="terms__sec">
                            <h2>קבצי <bdi>Cookie</bdi></h2>
                            <ol class="terms__clauses" role="list">
                                <li>
                                    אנו משתמשים בקבצי <bdi>Cookie</bdi> ומנתחים נתונים לצורך שיפור חווית המשתמש והשירות.
                                </li>
                                <li>המידע הנ״ל לא יועבר לצדדים שלישיים ולא נעשה כל שימוש בנתונים אישיים.</li>
                            </ol>
                        </section>
                        <section class="terms__sec">
                            <h2>הגבלות גיל ובקרת הורים</h2>
                            <ol class="terms__clauses" role="list">
                                <li>
                                    השימוש בשירות לקטינים מתחת לגיל <bdi>18</bdi> מחייב אישור והשגחה של הורה או
                                    אפוטרופוס.
                                </li>
                            </ol>
                        </section>
                        <section class="terms__sec">
                            <h2>כוח עליון והגבלת אחריות</h2>
                            <ol class="terms__clauses" role="list">
                                <li>
                                    <bdi>APPRU</bdi> אינה אחראית לאי מילוי התחייבויות עקב כוח עליון: אסונות טבע, מלחמות,
                                    פיגועים, התקפות סייבר, החלטות רשויות.
                                </li>
                                <li><bdi>APPRU</bdi> אינה אחראית לנזקים עקיפים, מקריים או מיוחדים.</li>
                            </ol>
                        </section>
                        <section class="terms__sec">
                            <h2>סמכות שיפוט ודין חל</h2>
                            <ol class="terms__clauses" role="list">
                                <li>על תנאים אלו חל הדין הישראלי בלבד.</li>
                                <li>סמכות השיפוט הבלעדית היא לבתי המשפט במחוז תל אביב.</li>
                            </ol>
                        </section>
                        <section class="terms__sec">
                            <h2>הוראות נוספות</h2>
                            <ol class="terms__clauses" role="list">
                                <li>אם איזו הוראה מתנאים אלו תוכרז כבלתי תקפה, יתר ההוראות יישארו בתוקף.</li>
                                <li>תנאים אלו מהווים את ההסכם השלם בין הצדדים בנושא השימוש בשירות.</li>
                            </ol>
                        </section>
                        <section class="terms__sec">
                            <h2>הפרת תנאי השימוש</h2>
                            <p class="terms__prose">
                                המשתמש מתחייב לפעול בהתאם להוראות תנאי שימוש אלו ולכל דין. החברה שומרת לעצמה את הזכות
                                לחסום את גישתו של משתמש לשירות, להסיר תכנים, להשעות או להפסיק את מתן השירות – באופן זמני
                                או קבוע, מיידי או לאחר התראה – במקרה של הפרה בפועל או חשש להפרה של תנאי השימוש, הוראות
                                הדין, או במקרה של שימוש לרעה בשירות, לרבות פגיעה בזכויות צדדים שלישיים, פגיעה באבטחת
                                המערכת או בתפקודה התקין.
                            </p>
                        </section>
                    </div>
                </div>
                <div class="popup-terms__fade popup-terms__fade_top" :class="{ 'is-visible': isScrollableUp }"></div>
                <div
                    class="popup-terms__fade popup-terms__fade_bottom"
                    :class="{ 'is-visible': isScrollableDown }"
                ></div>
            </LrudNode>

            <div class="popup-terms__footer">
                <div class="popup-terms__hint" :class="{ 'is-active': isDocFocused }">
                    <ArrowUpSvg class="popup-terms__hint-icon" />
                    <ArrowDownSvg class="popup-terms__hint-icon" />
                    <span>— прокрутка</span>
                </div>
                <LrudNode isFocusable @selected="onClose" @focused="focusTarget = 'button'">
                    <ButtonBase class="popup-terms__btn" @click="onClose">Закрыть</ButtonBase>
                </LrudNode>
            </div>
        </LrudNode>
    </PopupBase>
</template>

<style lang="scss" scoped>
.popup-terms {
    // Тёмная палитра из TV-версии исходного документа
    $ink: #e8e4da;
    $ink-soft: #ada79a;
    $rule: #3a362d;

    display: grid;
    grid-template-rows: 1fr auto;
    width: px(1240);
    height: px(760);
    color: $ink;

    &__scroll {
        position: relative;
        // Иначе строка грида растягивается по содержимому и прокрутки не будет
        min-height: 0;
    }

    &__doc {
        height: 100%;
        overflow-y: auto;
        // Место под скроллбар: при dir="rtl" он слева
        padding-left: px(24);
        scrollbar-width: thin;
        scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
        -webkit-overflow-scrolling: touch;

        &::-webkit-scrollbar {
            width: px(6);
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background-color: rgba(255, 255, 255, 0.15);
            border-radius: px(4);
        }
    }

    &__fade {
        position: absolute;
        left: 0;
        right: 0;
        height: px(56);
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.18s ease;

        &.is-visible {
            opacity: 1;
        }

        &_top {
            top: 0;
            background: linear-gradient(to bottom, $dark, rgba(20, 20, 20, 0));
        }

        &_bottom {
            bottom: 0;
            background: linear-gradient(to top, $dark, rgba(20, 20, 20, 0));
        }
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: px(24);
        padding-top: px(28);
        border-top: px(2) solid $rule;
    }

    &__hint {
        display: flex;
        align-items: center;
        gap: px(4);
        color: $ink-soft;
        font-size: px(20);
        // Гаснет, когда фокус ушёл на кнопку и стрелки больше не прокручивают
        opacity: 0.45;
        transition: opacity 0.18s ease;

        &.is-active {
            opacity: 1;
        }

        span {
            padding-left: px(4);
        }
    }

    &__hint-icon {
        width: px(24);
        height: px(24);
    }

    &__btn {
        width: px(260);
        background-color: transparent;
        outline: px(4) solid $main-black;

        .lrud_focused & {
            outline: px(4) solid $blue;
        }
    }

    /* ------------------------------------------------------------ документ */

    .terms {
        max-width: px(840);
        margin: 0 auto;
        // Шрифт базовый, проектный: иврит добирается системным fallback'ом
        font-size: px(24);
        font-weight: 400;
        // Иврит плотный и прямоугольный: воздуха нужно больше, чем латинице
        line-height: 1.75;
        // Никогда не justify: буквы иврита не растягиваются, в тексте появляются «реки»
        text-align: start;
        // Не даём RTL-документу влиять на LTR-раскладку приложения
        unicode-bidi: isolate;
        counter-reset: sec;

        h1,
        h2 {
            font-family: 'Montserrat', sans-serif;
            // В общем reset у заголовков font-weight: 400
            font-weight: 700;
        }

        h1 {
            font-size: px(38);
            line-height: 1.35;
            padding-bottom: px(22);
            margin-bottom: px(26);
            border-bottom: px(2) solid $rule;
        }

        // У иврита нет курсива: синтетический наклон читается как дефект отрисовки
        em,
        i,
        cite {
            font-style: normal;
            font-weight: 700;
        }

        &__intro {
            margin-bottom: px(36);
        }

        &__sec {
            margin-bottom: px(34);
            counter-increment: sec;
            counter-reset: sub;

            h2 {
                font-size: px(28);
                line-height: 1.35;
                margin-bottom: px(14);

                &:before {
                    /*
                     * Точка встаёт визуально левее цифры — это правильно: при чтении
                     * справа налево получается «1», «.», затем иврит. Не «чинить».
                     */
                    content: counter(sec) '. ';
                }
            }
        }

        &__clauses {
            list-style: none;

            li {
                position: relative;
                // Номер пункта висит в поле, перенос строки идёт под текстом
                padding-right: 3em;
                margin-bottom: px(12);
                counter-increment: sub;

                &:before {
                    content: counter(sec) '.' counter(sub);
                    position: absolute;
                    top: 0;
                    right: 0;
                    color: $ink-soft;
                }
            }
        }

        // Приставка и аббревиатура не должны разрываться по дефису
        &__nb {
            white-space: nowrap;
        }
    }
}
</style>
